import { useState, useEffect } from "react"
import ViewProductsList from "overrides/theme/wom/base/components/OrderDetail/ViewProductsList/ViewProductsList"
import { useParams } from "react-router-dom"
import useGetOrderDetail from "hooks/UseGetOrderDetail"
import ErrorHandler from "components/Commons/Error/ErrorHandler"
import { TransactionCodeEnum } from "enums/TransactionCodeEnum"
import {
  LoadingOverlay,
  SpinnerRing,
  LoadingTextWrapper,
  LoadingTitle,
  LoadingSubtitle,
  ProgressBarTrack,
  ProgressBarFill,
} from "overrides/theme/wom/base/Pages/OrderSearch/OrderSearchScreen.styled"
import { GridOrderDetailWrapper, ProductRow, ProductsContainer, ProductsFlexWrapperInfo, ProductsWrapperContainer } from "../../../components/OrderDetail/ViewProductsList/ViewProductsList.styled"
import { t } from "i18next"
import { formatDisplayDate } from "helpers/FormatDateHelper"
import { serviceListenerSearch } from "services/ListenerServices"
import { useCancelToken } from "hooks"

const ViewOrderDetailScreen = () => {
  const [trackingHistory, setTrackingHistory] = useState<any[]>([]);
  const { id, shippingGroupId }: any = useParams()
  const { shippingGroupDetail, order } = useGetOrderDetail(id, shippingGroupId)
  const [loading, setLoading] = useState(true)
  const { newCancelToken } = useCancelToken()

  const formatStringFull = "dd/MM/yyyy HH:mm"

  const ALLOWED_COURIERS = new Set([
    "SROUTE",
    "99 MIN",
    "CORHEX",
  ]);

  const isAllowedCourier = ALLOWED_COURIERS.has(shippingGroupDetail?.custom?.courier ?? "");

  useEffect(() => {
    if (order && shippingGroupDetail) {
      setLoading(false)
    }
  }, [order, shippingGroupDetail]);

  useEffect(() => {
    const loadTracking = async () => {

      const body = {
        serviceType: "OTSTATUS",
        rut: shippingGroupDetail?.custom?.customer?.id,
        orderId: shippingGroupDetail?.orderId,
      };

      try {
        const ALLOWED_CODES = [
          "IN_TRANSIT/",
          "CANCELED",
          "DELIVERED",
        ];

        const {
          data: { code, message },
        } = await serviceListenerSearch(body, newCancelToken())

        if (code === TransactionCodeEnum.ok && Array.isArray(message)) {
          const filteredData = message.filter((item: any) =>
            ALLOWED_CODES.some((code: string) => item.code.startsWith(code))
          ).map((item: any) => ({
            ...item,
            name: item.code
              .replace(/^IN_TRANSIT\//, "")
              .replace(/^CANCELED.*$/, "Cancelado")
              .replace(/^DELIVERED.*$/, "Entregado"),
          }));
          setTrackingHistory(filteredData);
        }
      } catch (error) {
        console.error(error);
      }
    };

    if (isAllowedCourier)
      loadTracking();
  },
    //eslint-disable-next-line
    [isAllowedCourier, shippingGroupDetail]);

  if (loading) {
    return (
      <LoadingOverlay>
        <SpinnerRing />
        <LoadingTextWrapper>
          <LoadingTitle>Buscando detalles de la orden…</LoadingTitle>
          <LoadingSubtitle>Estamos consultando la información, esto puede tomar unos segundos.</LoadingSubtitle>
        </LoadingTextWrapper>
        <ProgressBarTrack>
          <ProgressBarFill />
        </ProgressBarTrack>
      </LoadingOverlay>
    )
  }

  const hasData = !!(order && shippingGroupDetail)

  if (!hasData) {
    return (
      <ErrorHandler
        code={TransactionCodeEnum.notFound}
        message="No se pudo obtener la información de la orden o Shipping Group. Por favor, intente de nuevo o verifique los datos."
      />
    )
  }

  const itemsOrder = shippingGroupDetail?.source?.items
  const status = shippingGroupDetail?.status

  return (
    <>
      <GridOrderDetailWrapper>
        {itemsOrder && (
          <div className="column">
            <ViewProductsList
              itemsOrder={itemsOrder}
              titleMain={"Productos"}
            />
          </div>
        )}
        {shippingGroupDetail && (
          <div className="column">
            <ProductsContainer>
              <div className="products-title-banner">
                <span>Detalle de SG</span>
              </div>
              <ProductsWrapperContainer>
                {shippingGroupDetail?.id &&
                  <ProductRow>
                    <div className="field-row">
                      <span className="field-label">SG:</span>
                      <span className="field-value">{shippingGroupDetail?.id}</span>
                    </div>
                  </ProductRow>
                }
                {shippingGroupDetail?.currentStatus?.tag &&
                  <ProductRow>
                    <div className="field-row">
                      <span className="field-label">Estado Actual:</span>
                      <span className="field-value">{t(`${shippingGroupDetail?.currentStatus?.tag}.subTitle`)}</span>
                    </div>
                  </ProductRow>
                }
                {shippingGroupDetail?.shippingType &&
                  <ProductRow>
                    <div className="field-row">
                      <span className="field-label">Tipo de Despacho:</span>
                      <span className="field-value">{t(shippingGroupDetail?.shippingType)}</span>
                    </div>
                  </ProductRow>
                }
                {shippingGroupDetail?.custom?.deliveryType &&
                  <ProductRow>
                    <div className="field-row">
                      <span className="field-label">Modalidad:</span>
                      <span className="field-value">{t(shippingGroupDetail?.custom?.deliveryType)}</span>
                    </div>
                  </ProductRow>
                }
                {(shippingGroupDetail?.source?.name || shippingGroupDetail?.source?.id) &&
                  <ProductRow>
                    <div className="field-row">
                      <span className="field-label">Tienda Origen:</span>
                      <span className="field-value">{shippingGroupDetail?.source?.name || shippingGroupDetail?.source?.id}</span>
                    </div>
                  </ProductRow>
                }
                {(shippingGroupDetail?.target?.source?.id || shippingGroupDetail?.target?.source?.name) &&
                  <ProductRow>
                    <div className="field-row">
                      <span className="field-label">Tienda Destino:</span>
                      <span className="field-value">{shippingGroupDetail?.target?.source?.name || shippingGroupDetail?.target?.source?.id}</span>
                    </div>
                  </ProductRow>
                }
              </ProductsWrapperContainer>
            </ProductsContainer>
          </div>
        )}
      </GridOrderDetailWrapper>

      <GridOrderDetailWrapper>
        {shippingGroupDetail?.status && status.length > 0 && (
          <div className="column">
            <ProductsContainer>
              <div className="products-title-banner">
                <span>Estados de la SG</span>
              </div>
              <ProductsFlexWrapperInfo>
                <div className="row">
                  <div className="col">
                    <span className="title">Estado</span>
                  </div>
                  <div className="col">
                    <span className="title">Fecha</span>
                  </div>
                </div>
                {status.map((item, index) => (
                  <div key={index} className="row">
                    <div className="col">
                      <span className="info">{t(`${item.tag}.subTitle`)}</span>
                    </div>
                    <div className="col">
                      <span className="info">{formatDisplayDate(item.date, formatStringFull)}</span>
                    </div>
                  </div>
                ))}
              </ProductsFlexWrapperInfo>
            </ProductsContainer>
          </div>
        )}
        {order && (
          <div className="column">
            <ProductsContainer>
              <div className="products-title-banner">
                <span>Detalle de Orden</span>
              </div>
              <ProductsWrapperContainer>
                {order?.creationDate &&
                  <ProductRow>
                    <div className="field-row">
                      <span className="field-label">Fecha de Creación:</span>
                      <span className="field-value">{formatDisplayDate(order?.creationDate)}</span>
                    </div>
                  </ProductRow>
                }
                {(order?.custom?.estimatedDeliveryDate2 || order?.estimatedDeliveryDate) &&
                  <ProductRow>
                    <div className="field-row">
                      <span className="field-label">Fecha Estimada de Entrega:</span>
                      <span className="field-value">
                        {order?.custom?.estimatedDeliveryDate2 ? formatDisplayDate(order?.custom?.estimatedDeliveryDate2) : formatDisplayDate(order?.estimatedDeliveryDate)}
                      </span>
                    </div>
                  </ProductRow>
                }
                {order?.channel &&
                  <ProductRow>
                    <div className="field-row">
                      <span className="field-label">Canal de Ventas:</span>
                      <span className="field-value">{order?.channel}</span>
                    </div>
                  </ProductRow>
                }
              </ProductsWrapperContainer>
            </ProductsContainer>
          </div>
        )}
      </GridOrderDetailWrapper>

      <GridOrderDetailWrapper>
        {isAllowedCourier && (
          <div className="column">
            <ProductsContainer>
              <div className="products-title-banner">
                <span>Estados del Repartidor</span>
              </div>
              {trackingHistory?.length > 0 && (
                <ProductsWrapperContainer>
                  <ProductRow>
                    <div className="field-row">
                      <span className="field-label">Nombre:</span>
                      <span className="field-value">{shippingGroupDetail?.custom?.courier || ""}</span>
                    </div>
                  </ProductRow>
                  <ProductsFlexWrapperInfo>
                    <div className="row">
                      <div className="col">
                        <span className="title">Estado</span>
                      </div>
                      <div className="col">
                        <span className="title">Fecha</span>
                      </div>
                    </div>
                  </ProductsFlexWrapperInfo>
                  {trackingHistory.map((item: any, index: number) => (
                    <ProductRow key={index}>
                      <div className="field-row">
                        <span className="field-value">{item?.name}</span>
                        <span className="field-value text-right">{formatDisplayDate(item?.date, formatStringFull)}</span>
                      </div>
                    </ProductRow>
                  ))}
                </ProductsWrapperContainer>
              )}
            </ProductsContainer>
          </div>
        )}
        {order?.shippingAddress && (
          <div className="column">
            <ProductsContainer>
              <div className="products-title-banner">
                <span>Datos de Entrega</span>
              </div>
              <ProductsWrapperContainer>
                <ProductRow>
                  <div className="field-row">
                    <span className="field-label">Cliente Titular:</span>
                    <span className="field-value">{order.customer?.name || ""}</span>
                  </div>
                </ProductRow>
                <ProductRow>
                  <div className="field-row">
                    <span className="field-label">Dirección:</span>
                    <span className="field-value">{order?.shippingAddress?.address || ""}</span>
                  </div>
                </ProductRow>
                <ProductRow>
                  <div className="field-row">
                    <span className="field-label">Comuna:</span>
                    <span className="field-value">{order?.shippingAddress?.location?.commune || ""}</span>
                  </div>
                </ProductRow>
                <ProductRow>
                  <div className="field-row">
                    <span className="field-label">Región:</span>
                    <span className="field-value">{order?.shippingAddress?.location?.region || ""}</span>
                  </div>
                </ProductRow>
                <ProductRow>
                  <div className="field-row">
                    <span className="field-label">Nombre de Contacto:</span>
                    <span className="field-value">{order.shippingAddress?.contact?.name || ""}</span>
                  </div>
                </ProductRow>
                <ProductRow>
                  <div className="field-row">
                    <span className="field-label">Teléfono de Contacto:</span>
                    <span className="field-value">{order.shippingAddress?.contact?.phone || ""}</span>
                  </div>
                </ProductRow>
                {order.shippingAddress?.custom?.recibeTercero === "SI" &&
                  <>
                    <ProductRow>
                      <div className="field-row">
                        <span className="field-label">Tercero Autorizado:</span>
                        <span className="field-value">SI</span>
                      </div>
                    </ProductRow>
                    <ProductRow>
                      <div className="field-row">
                        <span className="field-label">Nombre:</span>
                        <span className="field-value">{`${order.shippingAddress?.custom?.nombreTercero || ""} ${order.shippingAddress?.custom?.apellidoTercero || ""}`}</span>
                      </div>
                    </ProductRow>
                    <ProductRow>
                      <div className="field-row">
                        <span className="field-label">RUT:</span>
                        <span className="field-value">{order.shippingAddress?.custom?.rutTercero || ""}</span>
                      </div>
                    </ProductRow>
                  </>
                }
              </ProductsWrapperContainer>
            </ProductsContainer>
          </div>
        )}
      </GridOrderDetailWrapper>
    </>
  )
}

export default ViewOrderDetailScreen
